import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import BackButton from '@/components/BackButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  BookOpen, Plus, Edit2, Trash2, Share2, Calendar, 
  Sparkles, FileText, MessageSquare, Diamond, Heart,
  Lightbulb, Loader2, Quote, HelpCircle, RefreshCw,
  TrendingUp, Target, Zap, ArrowRight, BarChart3, Mic
} from 'lucide-react';
import VoiceInput from '@/components/voice/VoiceInput';
import TextToSpeech from '@/components/voice/TextToSpeech';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JournalPage = () => {
  const { user } = useAuth();
  const { celebrate, quickCelebrate } = useCelebration();
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    entry_type: 'note',
    share_with_coach: false
  });
  const [saving, setSaving] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Support prompt from "I Need Support" flow
  const [supportPrompt, setSupportPrompt] = useState(null);
  
  // Discernment Tool state
  const [discernmentLoading, setDiscernmentLoading] = useState(false);
  const [discernmentInsight, setDiscernmentInsight] = useState(null);
  const [showDiscernmentDialog, setShowDiscernmentDialog] = useState(false);
  
  // Journey Patterns state
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [journeyPatterns, setJourneyPatterns] = useState(null);
  const [showPatternsDialog, setShowPatternsDialog] = useState(false);
  
  // AI Prompt Suggestions state
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(false);

  useEffect(() => {
    fetchEntries();
    
    // Check if we came from "I Need Support" with a prompt
    if (location.state?.supportPrompt) {
      setSupportPrompt({
        prompt: location.state.supportPrompt,
        type: location.state.supportType
      });
    }
  }, [location.state]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/journal`);
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch journal entries:', error);
      toast.error('Failed to load journal');
    } finally {
      setLoading(false);
    }
  };

  const openNewEntry = (withSupportPrompt = false) => {
    setEditingEntry(null);
    if (withSupportPrompt && supportPrompt) {
      setFormData({
        title: `${supportPrompt.type} Reflection`,
        content: '',
        entry_type: 'reflection',
        share_with_coach: false,
        prompt: supportPrompt.prompt
      });
    } else {
      setFormData({
        title: '',
        content: '',
        entry_type: 'note',
        share_with_coach: false
      });
    }
    setSuggestedPrompts([]); // Reset prompts
    setShowDialog(true);
    
    // Fetch AI prompts when opening new entry dialog (if no support prompt)
    if (!supportPrompt) {
      getSuggestedPrompts();
    }
  };

  const dismissSupportPrompt = () => {
    setSupportPrompt(null);
    // Clear the location state
    window.history.replaceState({}, document.title);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title,
      content: entry.content,
      entry_type: entry.entry_type,
      share_with_coach: entry.share_with_coach
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    setSaving(true);
    try {
      if (editingEntry) {
        await axios.put(`${API_URL}/api/journal/${editingEntry.id}`, formData);
        toast.success('Entry updated');
      } else {
        await axios.post(`${API_URL}/api/journal`, formData);
        quickCelebrate('Soul check-in complete. +10 diamonds added to your joy bank!');
        toast.success('Entry created! +10 diamonds added to your joy bank');
      }
      setShowDialog(false);
      fetchEntries();
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await axios.delete(`${API_URL}/api/journal/${entryId}`);
      toast.success('Entry deleted');
      fetchEntries();
      if (selectedEntry?.id === entryId) {
        setSelectedEntry(null);
      }
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const getEntryTypeIcon = (type) => {
    switch (type) {
      case 'reflection_response': return <Sparkles className="h-4 w-4" />;
      case 'coaching_note': return <MessageSquare className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getEntryTypeLabel = (type) => {
    switch (type) {
      case 'reflection_response': return 'Reflection';
      case 'coaching_note': return 'Coaching Note';
      default: return 'Note';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Discernment Tool - Get AI coaching insights
  const getDiscernment = async (entryId = null, content = null) => {
    if (!entryId && !content) {
      if (selectedEntry) {
        entryId = selectedEntry.id;
        content = selectedEntry.content;
      } else {
        toast.error('Please select or write an entry first');
        return;
      }
    }
    
    if (content && content.length < 20) {
      toast.error('Please write at least 20 characters for meaningful insights');
      return;
    }
    
    setDiscernmentLoading(true);
    setDiscernmentInsight(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/journal/discernment`, {
        entry_id: entryId,
        content: content
      });
      
      setDiscernmentInsight(response.data);
      setShowDiscernmentDialog(true);
      
      if (response.data.points_awarded > 0) {
        toast.success(`+${response.data.points_awarded} diamonds earned for using Discernment Tool!`);
      }
    } catch (error) {
      console.error('Discernment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get insights');
    } finally {
      setDiscernmentLoading(false);
    }
  };

  // Journey Patterns - Analyze patterns across multiple entries
  const getJourneyPatterns = async () => {
    if (entries.length < 3) {
      toast.error(`You need at least 3 journal entries for pattern analysis. You have ${entries.length}. Keep journaling!`);
      return;
    }
    
    setPatternsLoading(true);
    setJourneyPatterns(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/journal/journey-patterns`);
      
      setJourneyPatterns(response.data);
      setShowPatternsDialog(true);
      
      if (response.data.points_awarded > 0) {
        toast.success(`+${response.data.points_awarded} diamonds earned for Journey Patterns analysis!`);
      }
    } catch (error) {
      console.error('Journey Patterns error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze patterns');
    } finally {
      setPatternsLoading(false);
    }
  };

  // AI Prompt Suggestions - Get personalized prompts for new entries
  const getSuggestedPrompts = async () => {
    setPromptsLoading(true);
    setSuggestedPrompts([]);
    
    try {
      const response = await axios.get(`${API_URL}/api/journal/suggest-prompts`);
      setSuggestedPrompts(response.data.prompts || []);
    } catch (error) {
      console.error('Prompt suggestion error:', error);
      toast.error('Failed to get prompt suggestions');
    } finally {
      setPromptsLoading(false);
    }
  };

  // Select a suggested prompt
  const selectPrompt = (prompt) => {
    setFormData({
      ...formData,
      title: prompt.category.charAt(0).toUpperCase() + prompt.category.slice(1) + ' Entry',
      content: '',
      prompt: prompt.prompt // Store the prompt for display
    });
    setSuggestedPrompts([]); // Clear suggestions after selection
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="journal-page">
        <BackButton to="/dashboard" label="Back to Dashboard" />
        
        {/* Banner */}
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg relative h-40 md:h-48" style={{background: 'linear-gradient(135deg, #2D4739 0%, #2D4739 60%, #C4704B 100%)'}}>
          <div className="absolute inset-0 flex items-center justify-between px-6 md:px-10">
            <div>
              <p className="text-white text-sm md:text-base font-medium opacity-90">Soul Journal</p>
              <p className="text-white text-xl md:text-2xl font-heading font-bold">Pour Out What's Rising</p>
              <p className="text-white text-sm mt-1 opacity-80">Your sacred space for reflection</p>
            </div>
            <BookOpen className="h-16 w-16 md:h-20 md:w-20 text-white opacity-20" />
          </div>
        </div>
        
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest flex items-center gap-3">
              <BookOpen className="h-8 w-8" />
              Soul Journal
            </h1>
            <p className="text-gray-600 mt-2">
              What's rising in your heart? Pour it out here, Sis.
            </p>
          </div>
          <Button className="btn-primary" onClick={() => openNewEntry()} data-testid="new-entry-btn">
            <Plus className="h-4 w-4 mr-2" /> New Entry
          </Button>
        </div>

        {/* Support Prompt Card - from "I Need Support" flow */}
        {supportPrompt && (
          <Card className="mb-6 border-terracotta/30 bg-gradient-to-r from-terracotta/10 to-transparent">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-terracotta/20 flex items-center justify-center shrink-0">
                    <Heart className="h-6 w-6 text-terracotta" />
                  </div>
                  <div>
                    <p className="text-sm text-terracotta font-medium mb-1">
                      {supportPrompt.type} Support Reflection
                    </p>
                    <p className="text-forest font-heading text-lg italic">
                      "{supportPrompt.prompt}"
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      Take a moment to reflect on this prompt. Writing helps clarity.
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={dismissSupportPrompt}
                  className="text-gray-400 hover:text-gray-600 shrink-0"
                >
                  ✕
                </Button>
              </div>
              <div className="mt-4 flex gap-3">
                <Button 
                  onClick={() => openNewEntry(true)} 
                  className="bg-terracotta hover:bg-terracotta/90 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" /> Start Writing
                </Button>
                <Button 
                  variant="outline" 
                  onClick={dismissSupportPrompt}
                  className="border-terracotta/30 text-terracotta hover:bg-terracotta/10"
                >
                  Save for Later
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Diamond Points Notice */}
        <div className="mb-6 p-4 bg-purple-50 rounded-xl border border-purple-200">
          <div className="flex items-center gap-2 text-purple-700">
            <Diamond className="h-5 w-5" />
            <span className="font-medium">Earn 10 diamonds for each soul check-in!</span>
          </div>
        </div>

        {/* Journey Patterns Card - Only show when user has 3+ entries */}
        {entries.length >= 3 && (
          <Card className="mb-6 card-base bg-gradient-to-r from-forest/5 via-purple-50/50 to-terracotta/5 border-forest/20">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-forest to-purple-600 flex items-center justify-center shrink-0">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-forest flex items-center gap-2">
                      Journey Patterns
                      <Badge className="bg-purple-100 text-purple-700 text-xs">+15 💎</Badge>
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Discover recurring themes, growth areas, and transformation moments across your {entries.length} soul check-ins.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={getJourneyPatterns}
                  disabled={patternsLoading}
                  className="bg-forest hover:bg-forest/90 text-white shrink-0"
                  data-testid="journey-patterns-btn"
                >
                  {patternsLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing...</>
                  ) : (
                    <><TrendingUp className="h-4 w-4 mr-2" />Analyze My Journey</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Entries List */}
          <div className="lg:col-span-1">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-forest">
                  Entries ({entries.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-16 bg-sand rounded animate-pulse" />
                    ))}
                  </div>
                ) : entries.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No entries yet</p>
                    <p className="text-sm mt-1">Start pouring out your heart</p>
                  </div>
                ) : (
                  <div className="divide-y divide-sand">
                    {entries.map((entry) => (
                      <button
                        key={entry.id}
                        onClick={() => setSelectedEntry(entry)}
                        className={`w-full p-4 text-left hover:bg-sand/50 transition-colors ${
                          selectedEntry?.id === entry.id ? 'bg-sand/50 border-l-4 border-forest' : ''
                        }`}
                        data-testid={`entry-${entry.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-forest truncate">{entry.title}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs flex items-center gap-1">
                                {getEntryTypeIcon(entry.entry_type)}
                                {getEntryTypeLabel(entry.entry_type)}
                              </Badge>
                              {entry.share_with_coach && (
                                <Share2 className="h-3 w-3 text-terracotta" title="Shared with coach" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(entry.created_at)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Entry Detail */}
          <div className="lg:col-span-2">
            {selectedEntry ? (
              <Card className="card-base">
                <CardHeader className="flex flex-row items-start justify-between">
                  <div>
                    <CardTitle className="text-xl font-heading text-forest">
                      {selectedEntry.title}
                    </CardTitle>
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline" className="flex items-center gap-1">
                        {getEntryTypeIcon(selectedEntry.entry_type)}
                        {getEntryTypeLabel(selectedEntry.entry_type)}
                      </Badge>
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(selectedEntry.created_at)}
                      </span>
                      {selectedEntry.share_with_coach && (
                        <Badge className="bg-terracotta/10 text-terracotta">
                          <Share2 className="h-3 w-3 mr-1" />
                          Shared with Coach
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEditEntry(selectedEntry)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(selectedEntry.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-500">Your Entry</span>
                    <TextToSpeech 
                      entryId={selectedEntry.id}
                      label="Listen to Entry"
                    />
                  </div>
                  <div className="prose prose-forest max-w-none">
                    <p className="whitespace-pre-wrap text-gray-700">{selectedEntry.content}</p>
                  </div>
                  
                  {/* Discernment Tool Button */}
                  <div className="mt-6 pt-6 border-t border-sand">
                    <Button 
                      onClick={() => getDiscernment(selectedEntry.id)}
                      disabled={discernmentLoading}
                      className="w-full bg-gradient-to-r from-purple-600 to-terracotta hover:from-purple-700 hover:to-terracotta/90 text-white"
                      data-testid="discernment-btn"
                    >
                      {discernmentLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />SHE is reflecting...</>
                      ) : (
                        <><Lightbulb className="h-4 w-4 mr-2" />Ask SHE for Insight (Pocket Coach)</>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Get personalized coaching insights from your AI guide
                    </p>
                  </div>
                  
                  {selectedEntry.updated_at !== selectedEntry.created_at && (
                    <p className="text-xs text-gray-400 mt-6">
                      Last updated: {formatDate(selectedEntry.updated_at)}
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="card-base">
                <CardContent className="py-16 text-center text-gray-500">
                  <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="font-heading text-xl text-forest mb-2">Select an entry</h3>
                  <p>Click on an entry to view what you poured out, or start a new soul check-in.</p>
                </CardContent>
              </Card>
            )}
            
            {/* Journaling Inspiration Image - Below Entry Detail */}
            <div className="mt-6 rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_serene-edison/artifacts/a49syj8r_JOurnal.jpeg"
                alt="Sister Diamond journaling"
                className="w-full h-auto object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>

        {/* New/Edit Entry Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setSuggestedPrompts([]);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                {editingEntry ? 'Edit Entry' : 'New Journal Entry'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* AI Prompt Suggestions - Only show for new entries without existing prompt */}
              {!editingEntry && !formData.prompt && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      SHE suggests...
                    </p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={getSuggestedPrompts}
                      disabled={promptsLoading}
                      className="text-xs"
                    >
                      {promptsLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <><RefreshCw className="h-3 w-3 mr-1" />New prompts</>
                      )}
                    </Button>
                  </div>
                  
                  {promptsLoading ? (
                    <div className="flex items-center justify-center py-6 text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      <span className="text-sm">Finding the right questions for you...</span>
                    </div>
                  ) : suggestedPrompts.length > 0 ? (
                    <div className="space-y-2">
                      {suggestedPrompts.map((prompt, i) => (
                        <button
                          key={i}
                          onClick={() => selectPrompt(prompt)}
                          className="w-full text-left p-3 rounded-lg border border-purple-100 bg-gradient-to-r from-purple-50/50 to-transparent hover:from-purple-100/50 hover:border-purple-200 transition-all group"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-white text-xs ${
                              prompt.category === 'reflection' ? 'bg-purple-500' :
                              prompt.category === 'gratitude' ? 'bg-green-500' :
                              prompt.category === 'intention' ? 'bg-blue-500' :
                              prompt.category === 'release' ? 'bg-amber-500' :
                              'bg-terracotta'
                            }`}>
                              {prompt.category === 'reflection' ? '?' :
                               prompt.category === 'gratitude' ? '♥' :
                               prompt.category === 'intention' ? '→' :
                               prompt.category === 'release' ? '↑' : '★'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-forest font-medium leading-snug">{prompt.prompt}</p>
                              <p className="text-xs text-gray-500 mt-1 italic">{prompt.why}</p>
                            </div>
                            <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-purple-600 shrink-0 mt-1" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Click "New prompts" for personalized suggestions
                    </p>
                  )}
                  
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-xs text-gray-500 text-center">Or write freely below</p>
                  </div>
                </div>
              )}
              
              {/* Show support prompt if coming from "I Need Support" */}
              {formData.prompt && (
                <div className="p-4 bg-terracotta/10 rounded-xl border border-terracotta/20">
                  <p className="text-sm text-terracotta font-medium mb-1">Your Prompt</p>
                  <p className="text-forest italic">&ldquo;{formData.prompt}&rdquo;</p>
                </div>
              )}
              
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Give your entry a title..."
                  className="input-base"
                />
              </div>

              <div>
                <Label htmlFor="entry_type">Entry Type</Label>
                <select
                  id="entry_type"
                  value={formData.entry_type}
                  onChange={(e) => setFormData({ ...formData, entry_type: e.target.value })}
                  className="w-full p-2 border rounded-lg input-base"
                >
                  <option value="note">Daily Note</option>
                  <option value="reflection_response">Reflection Response</option>
                  <option value="coaching_note">Coaching Note</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="content">What's Rising in Your Heart? *</Label>
                  <VoiceInput 
                    onTranscript={(text) => setFormData(prev => ({ 
                      ...prev, 
                      content: prev.content ? `${prev.content}\n\n${text}` : text 
                    }))}
                    disabled={saving}
                  />
                </div>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Pour out your thoughts, reflections, or what needs to be released... Or click 'Record Voice' to speak your entry."
                  className="input-base min-h-48"
                />
              </div>

              {user?.matched_coach_id && (
                <div className="flex items-center justify-between p-4 bg-sand/30 rounded-lg">
                  <div>
                    <Label>Share with your guide</Label>
                    <p className="text-sm text-gray-500">Your coach will be able to see this entry</p>
                  </div>
                  <Switch
                    checked={formData.share_with_coach}
                    onCheckedChange={(checked) => setFormData({ ...formData, share_with_coach: checked })}
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="btn-primary" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Create Entry')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Discernment Tool Dialog */}
        <Dialog open={showDiscernmentDialog} onOpenChange={setShowDiscernmentDialog}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-purple-600" />
                SHE's Insight
              </DialogTitle>
            </DialogHeader>
            
            {discernmentInsight && (
              <div className="space-y-6 py-4">
                {/* Listen to Insight */}
                <div className="flex justify-end">
                  <TextToSpeech 
                    text={`${discernmentInsight.insight} ${discernmentInsight.affirmation ? `Here is your truth to carry: ${discernmentInsight.affirmation}` : ''}`}
                    label="Listen to Insight"
                  />
                </div>
                
                {/* Main Insight */}
                <div className="p-4 bg-gradient-to-r from-purple-50 to-terracotta/10 rounded-xl border border-purple-200">
                  <p className="text-sm text-purple-700 font-medium mb-2 flex items-center gap-2">
                    <Quote className="h-4 w-4" />
                    What I Notice
                  </p>
                  <p className="text-forest leading-relaxed">{discernmentInsight.insight}</p>
                </div>
                
                {/* Reflection Questions */}
                {discernmentInsight.reflection_questions?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 font-medium mb-3 flex items-center gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Questions for Deeper Reflection
                    </p>
                    <div className="space-y-2">
                      {discernmentInsight.reflection_questions.map((q, i) => (
                        <div key={i} className="p-3 bg-sand/30 rounded-lg border-l-4 border-terracotta/50">
                          <p className="text-forest text-sm italic">{q}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Affirmation */}
                {discernmentInsight.affirmation && (
                  <div className="p-4 bg-forest/5 rounded-xl border border-forest/20 text-center">
                    <p className="text-sm text-forest/70 font-medium mb-1">Your Truth to Carry</p>
                    <p className="text-forest font-heading text-lg">
                      "{discernmentInsight.affirmation}"
                    </p>
                  </div>
                )}
                
                {/* Focus Area Badge */}
                {discernmentInsight.focus_area && discernmentInsight.focus_area !== 'general' && (
                  <div className="text-center">
                    <Badge className="bg-purple-100 text-purple-700">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Personalized for your {discernmentInsight.focus_area.replace(/_/g, ' ')} journey
                    </Badge>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => getDiscernment(selectedEntry?.id)}
                disabled={discernmentLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${discernmentLoading ? 'animate-spin' : ''}`} />
                Get New Insight
              </Button>
              <Button 
                onClick={() => setShowDiscernmentDialog(false)}
                className="bg-forest hover:bg-forest/90 text-white"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Journey Patterns Dialog */}
        <Dialog open={showPatternsDialog} onOpenChange={setShowPatternsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-forest" />
                Your Journey Patterns
              </DialogTitle>
              {journeyPatterns && (
                <p className="text-sm text-gray-500">
                  Analysis of {journeyPatterns.entries_analyzed} entries from{' '}
                  {journeyPatterns.date_range?.from} to {journeyPatterns.date_range?.to}
                </p>
              )}
            </DialogHeader>
            
            {journeyPatterns && (
              <div className="space-y-6 py-4">
                {/* Recurring Themes */}
                {journeyPatterns.themes?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-forest mb-3 flex items-center gap-2">
                      <Target className="h-4 w-4 text-terracotta" />
                      Recurring Themes in Your Journey
                    </h4>
                    <div className="grid gap-3">
                      {journeyPatterns.themes.map((theme, i) => (
                        <div key={i} className="p-3 bg-gradient-to-r from-terracotta/5 to-transparent rounded-lg border border-terracotta/20">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-forest">{theme.name}</span>
                            <Badge variant="outline" className={`text-xs ${
                              theme.frequency === 'often' ? 'bg-green-50 text-green-700 border-green-200' :
                              theme.frequency === 'sometimes' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              'bg-blue-50 text-blue-700 border-blue-200'
                            }`}>
                              {theme.frequency}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">{theme.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Growth Areas */}
                {journeyPatterns.growth_areas?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-forest mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      Your Growth Areas
                    </h4>
                    <div className="space-y-3">
                      {journeyPatterns.growth_areas.map((area, i) => (
                        <div key={i} className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="font-medium text-green-800">{area.area}</p>
                          <p className="text-sm text-green-700 mt-1">{area.evidence}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transformation Moments */}
                {journeyPatterns.transformation_moments?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-forest mb-3 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-purple-600" />
                      Transformation Moments
                    </h4>
                    <div className="space-y-3">
                      {journeyPatterns.transformation_moments.map((moment, i) => (
                        <div key={i} className="p-4 bg-gradient-to-r from-purple-50 to-terracotta/10 rounded-xl border border-purple-200">
                          <p className="font-medium text-forest">{moment.moment}</p>
                          <p className="text-sm text-purple-700 mt-2 italic">
                            <Sparkles className="h-3 w-3 inline mr-1" />
                            {moment.significance}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Emotional Journey */}
                {journeyPatterns.emotional_journey && (
                  <div className="p-4 bg-sand/50 rounded-xl border border-sand">
                    <h4 className="font-medium text-forest mb-2 flex items-center gap-2">
                      <Heart className="h-4 w-4 text-terracotta" />
                      Your Emotional Journey
                    </h4>
                    <p className="text-gray-700 leading-relaxed">{journeyPatterns.emotional_journey}</p>
                  </div>
                )}

                {/* Recommendation */}
                {journeyPatterns.recommendation && (
                  <div className="p-4 bg-forest/5 rounded-xl border border-forest/20">
                    <h4 className="font-medium text-forest mb-2 flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-forest" />
                      Your Next Step
                    </h4>
                    <p className="text-forest font-heading">{journeyPatterns.recommendation}</p>
                  </div>
                )}

                {/* Focus Area Badge */}
                {journeyPatterns.focus_area && journeyPatterns.focus_area !== 'general' && (
                  <div className="text-center pt-2">
                    <Badge className="bg-forest/10 text-forest">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Analyzed through your {journeyPatterns.focus_area.replace(/_/g, ' ')} lens
                    </Badge>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={getJourneyPatterns}
                disabled={patternsLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${patternsLoading ? 'animate-spin' : ''}`} />
                Refresh Analysis
              </Button>
              <Button 
                onClick={() => setShowPatternsDialog(false)}
                className="bg-forest hover:bg-forest/90 text-white"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default JournalPage;
